/* eslint-disable no-use-before-define */
/* eslint-disable newline-before-return */
import { buildOption } from "@/components/FormEngine/helpers/buildOptions";
import {
  FormField,
  FormFieldValue,
  FormOption,
  RawField,
} from "@/components/FormEngine/types";
import {
  getString,
  isObject,
  sanitizeIdPart,
  toBool,
  toNumber,
} from "@/components/FormEngine/utils";

// ========================================
// TYPES ET CONSTANTES
// ========================================

/**
 * Valeurs par défaut pour les positions dans le formulaire
 */
const DEFAULT_POSITION_VALUE = -1;

/**
 * Types de valeurs primitives acceptées pour un champ de formulaire
 */
type AcceptedFieldValue = string | number | boolean | null | undefined;

/**
 * Configuration pour la normalisation des identifiants
 */
const ID_NORMALIZATION_CONFIG = {
  /** Pattern pour remplacer les indices de tableau [0] par -0 */
  ARRAY_INDEX_PATTERN: /\[(\d+)\]/g,
  /** Pattern pour remplacer les points et underscores */
  DOTS_UNDERSCORES_PATTERN: /[._]+/g,
  /** Pattern pour compresser les tirets multiples */
  MULTIPLE_DASHES_PATTERN: /-+/g,
  /** Pattern pour supprimer les tirets en début et fin */
  TRIM_DASHES_PATTERN: /^-|-$/g,
} as const;

// ========================================
// FONCTION PRINCIPALE
// ========================================

/**
 * Construit un champ de formulaire complet à partir de données brutes
 *
 * Cette fonction transforme un champ brut (RawField) en un champ de formulaire
 * structuré (FormField) avec toutes les propriétés nécessaires pour le rendu
 * et la gestion dans l'interface utilisateur.
 *
 * Processus de construction :
 * 1. Génération d'un ID unique basé sur le chemin et le nom
 * 2. Extraction des métadonnées de position (étape, ligne, colonne, etc.)
 * 3. Normalisation de la valeur du champ
 * 4. Configuration des propriétés booléennes (requis, modifiable, caché)
 * 5. Traitement des options si c'est un champ de sélection
 *
 * @param rawField - Les données brutes du champ provenant du backend
 * @param pathSegments - Le chemin vers ce champ dans la hiérarchie du formulaire
 * @returns Un champ de formulaire complètement structuré
 *
 * @example
 * const rawField = {
 *   name: "email",
 *   type: "email",
 *   value: "user@example.com",
 *   required: true,
 *   step: 1,
 *   row: 2,
 *   col: 1,
 *   section: 1,
 *   subSection: 2
 * };
 *
 * const formField = buildFormField(rawField, ["userInfo", "contact"]);
 *
 * // Résultat:
 * {
 *   id: "userInfo.contact.email",
 *   path: ["userInfo", "contact"],
 *   name: "email",
 *   type: "email",
 *   value: { id: "", value: "user@example.com" },
 *   required: true,
 *   writable: true,
 *   hidden: false,
 *   step: 1,
 *   row: 2,
 *   col: 1,
 *   section: 1,
 *   subSection: 2,
 *   initialField: { ... }
 * }
 */
export const buildFormField = (
  rawField: RawField,
  pathSegments: string[],
): FormField => {
  // Étape 1 : Générer l'identifiant unique du champ
  const fieldId = generateFieldId(pathSegments);

  // Étape 2 : Extraire les informations de base
  const basicInfo = extractBasicFieldInfo(rawField);

  // Étape 3 : Extraire les positions dans le formulaire
  const positions = extractFieldPositions(rawField);

  // Étape 4 : Préparer la valeur du champ
  const fieldValue = prepareFieldValue(rawField);

  // Étape 5 : Extraire les propriétés booléennes
  const booleanProperties = extractBooleanProperties(rawField);

  // Étape 6 : Extraire les propriétés optionnelles
  const optionalProperties = extractOptionalProperties(rawField);

  // Étape 7 : Construire le champ de base
  const baseField: FormField = {
    id: fieldId,
    path: pathSegments,
    ...basicInfo,
    ...positions,
    value: fieldValue,
    initialField: rawField,
    ...booleanProperties,
    ...optionalProperties,
  };

  // Étape 8 : Ajouter les options si nécessaire
  const fieldWithOptions = addOptionsIfPresent(baseField, rawField);

  return fieldWithOptions;
};

// ========================================
// GÉNÉRATION DE L'IDENTIFIANT
// ========================================

/**
 * Génère un identifiant unique pour le champ basé sur son chemin et son nom
 *
 * @param rawField - Le champ brut contenant le nom
 * @param pathSegments - Les segments du chemin
 * @returns L'identifiant unique du champ
 */
function generateFieldId(pathSegments: string[]): string {
  // Normaliser chaque segment du chemin
  const normalizedPathSegments = normalizePathSegments(pathSegments);

  // Combiner pour créer l'ID final
  const idParts = [...normalizedPathSegments].filter((part) => part.length > 0);

  return idParts.join(".");
}

/**
 * Normalise les segments du chemin pour l'ID
 *
 * @param pathSegments - Les segments à normaliser
 * @returns Les segments normalisés
 */
function normalizePathSegments(pathSegments: string[]): string[] {
  return pathSegments.map((segment) => {
    const sanitized = sanitizeIdPart(String(segment));
    return normalizeIdPartStrict(sanitized);
  });
}

/**
 * Normalise strictement une partie d'identifiant
 *
 * Transformations appliquées :
 * 1. [0] → -0 (indices de tableau)
 * 2. . et _ → - (points et underscores)
 * 3. -- → - (compression des tirets)
 * 4. Suppression des tirets en début/fin
 *
 * @param idPart - La partie d'ID à normaliser
 * @returns La partie normalisée
 *
 * @example
 * normalizeIdPartStrict("user_info[0].name") // "user-info-0-name"
 * normalizeIdPartStrict("--test--") // "test"
 */
const normalizeIdPartStrict = (idPart: string): string => {
  let normalizedId = idPart;

  // Remplacer [0] par -0
  normalizedId = normalizedId.replace(
    ID_NORMALIZATION_CONFIG.ARRAY_INDEX_PATTERN,
    "-$1",
  );

  // Remplacer points et underscores par des tirets
  normalizedId = normalizedId.replace(
    ID_NORMALIZATION_CONFIG.DOTS_UNDERSCORES_PATTERN,
    "-",
  );

  // Compresser les tirets multiples
  normalizedId = normalizedId.replace(
    ID_NORMALIZATION_CONFIG.MULTIPLE_DASHES_PATTERN,
    "-",
  );

  // Supprimer les tirets en début et fin
  normalizedId = normalizedId.replace(
    ID_NORMALIZATION_CONFIG.TRIM_DASHES_PATTERN,
    "",
  );

  return normalizedId;
};

// ========================================
// EXTRACTION DES INFORMATIONS
// ========================================

/**
 * Extrait les informations de base du champ
 *
 * @param rawField - Le champ brut
 * @returns Les informations de base
 */
function extractBasicFieldInfo(rawField: RawField): {
  name: string;
  type: string;
} {
  return {
    name: getString(rawField, "name"),
    type: getString(rawField, "type"),
  };
}

/**
 * Extrait les positions du champ dans le formulaire
 *
 * @param rawField - Le champ brut
 * @returns Les positions du champ
 */
function extractFieldPositions(rawField: RawField): {
  step: number;
  row: number;
  col: number;
  section: number;
  subSection: number;
} {
  return {
    step: extractStepPosition(rawField),
    row: extractRowPosition(rawField),
    col: extractColumnPosition(rawField),
    section: extractSectionPosition(rawField),
    subSection: extractSubSectionPosition(rawField),
  };
}

/**
 * Extrait la position de l'étape
 */
function extractStepPosition(rawField: RawField): number {
  return toNumber(rawField.step, DEFAULT_POSITION_VALUE);
}

/**
 * Extrait la position de la ligne
 */
function extractRowPosition(rawField: RawField): number {
  return toNumber(rawField.row, DEFAULT_POSITION_VALUE);
}

/**
 * Extrait la position de la colonne
 */
function extractColumnPosition(rawField: RawField): number {
  return toNumber(rawField.col, DEFAULT_POSITION_VALUE);
}

/**
 * Extrait la position de la section
 */
function extractSectionPosition(rawField: RawField): number {
  return toNumber(rawField.section, DEFAULT_POSITION_VALUE);
}

/**
 * Extrait la position de la sous-section
 * Gère les variations de nommage (subSection ou subsection)
 */
function extractSubSectionPosition(rawField: RawField): number {
  // Vérifier les deux variantes possibles du nom
  const subSectionValue = rawField.subSection ?? (rawField as any).subsection;
  return toNumber(subSectionValue, DEFAULT_POSITION_VALUE);
}

// ========================================
// TRAITEMENT DE LA VALEUR
// ========================================

/**
 * Prépare la valeur du champ pour le formulaire
 *
 * @param rawField - Le champ brut contenant la valeur
 * @returns La valeur formatée pour le formulaire
 */
function prepareFieldValue(rawField: RawField): FormFieldValue {
  const normalizedValue = normalizeFieldValue(rawField.value);

  return {
    id: "", // Peut être configuré si nécessaire
    value: normalizedValue,
  };
}

/**
 * Normalise la valeur d'un champ en restreignant aux types acceptés
 *
 * Types acceptés : string, number, boolean, null, undefined
 * Types rejetés : objects, arrays, functions → convertis en undefined
 *
 * @param rawValue - La valeur brute à normaliser
 * @returns La valeur normalisée ou undefined si non valide
 */
const normalizeFieldValue = (rawValue: unknown): AcceptedFieldValue => {
  // Accepter null et undefined tels quels
  if (rawValue === null || rawValue === undefined) {
    return rawValue;
  }

  // Vérifier le type et n'accepter que les primitives
  const valueType = typeof rawValue;

  switch (valueType) {
    case "string":
    case "number":
    case "boolean":
      return rawValue as AcceptedFieldValue;
    default:
      // Rejeter les objets, tableaux, fonctions, etc.
      return undefined;
  }
};

// ========================================
// EXTRACTION DES PROPRIÉTÉS
// ========================================

/**
 * Extrait les propriétés booléennes du champ
 *
 * @param rawField - Le champ brut
 * @returns Les propriétés booléennes avec leurs valeurs par défaut
 */
function extractBooleanProperties(rawField: RawField): {
  required: boolean;
  writable: boolean;
  hidden: boolean;
} {
  return {
    required: toBool(rawField.required, false),
    writable: toBool(rawField.writable, true),
    hidden: toBool(rawField.hidden, false),
  };
}

/**
 * Extrait les propriétés optionnelles du champ
 *
 * @param rawField - Le champ brut
 * @returns Les propriétés optionnelles si présentes
 */
function extractOptionalProperties(rawField: RawField): {
  decorator?: string;
} {
  const properties: { decorator?: string } = {};

  // Ajouter le décorateur s'il est valide
  if (isValidDecorator(rawField.decorator)) {
    properties.decorator = rawField.decorator as string;
  }

  return properties;
}

/**
 * Vérifie si une valeur est un décorateur valide
 *
 * @param decorator - La valeur à vérifier
 * @returns true si c'est une string valide
 */
function isValidDecorator(decorator: unknown): decorator is string {
  return typeof decorator === "string";
}

// ========================================
// GESTION DES OPTIONS
// ========================================

/**
 * Ajoute les options au champ si elles sont présentes
 *
 * @param field - Le champ de base
 * @param rawField - Le champ brut contenant potentiellement des options
 * @returns Le champ avec les options ajoutées si nécessaire
 */
function addOptionsIfPresent(field: FormField, rawField: RawField): FormField {
  // Vérifier si des options sont présentes
  if (!hasValidOptions(rawField)) {
    return field;
  }

  // Construire les options normalisées
  const normalizedOptions = buildFieldOptions(rawField.options as unknown[]);

  // Retourner le champ avec les options
  return {
    ...field,
    options: normalizedOptions,
  };
}

/**
 * Vérifie si le champ a des options valides
 *
 * @param rawField - Le champ à vérifier
 * @returns true si des options valides sont présentes
 */
function hasValidOptions(rawField: RawField): boolean {
  return Array.isArray(rawField.options);
}

/**
 * Construit les options normalisées pour le champ
 *
 * @param rawOptions - Les options brutes
 * @returns Les options normalisées
 */
function buildFieldOptions(rawOptions: unknown[]): FormOption[] {
  // Filtrer les objets valides et construire les options
  const validOptions = rawOptions.filter((option) => isObject(option));
  const normalizedOptions = validOptions.map((option) => buildOption(option));

  return normalizedOptions;
}
